// src/pages/RegistrationPage.js
// Updated: 06/20/2026

import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import Layout from '../components/common/layout/Layout';
import { useAuth } from '../context/AuthContext';
import { useFamily } from '../context/FamilyContext';
import { db } from '../firebase/config';
import { doc, getDoc, collection, getDocs, addDoc, serverTimestamp } from 'firebase/firestore';
import { v4 as uuidv4 } from 'uuid';
import toast from 'react-hot-toast';
import { format } from 'date-fns';
import Spinner from '../components/common/Spinner';
import MerchandiseSelector from '../components/registration/MerchandiseSelector';
import { FaTrash, FaPlus, FaLock, FaCalendarDay } from 'react-icons/fa';

const RegistrationPage = () => {
  const { eventId } = useParams();
  const navigate = useNavigate();
  const { userData } = useAuth();
  const { familyData } = useFamily();
  
  const [event, setEvent] = useState(null);
  const [merchandise, setMerchandise] = useState([]);
  const [attendees, setAttendees] = useState([{ id: uuidv4(), name: '', age: '', tshirtName: '', tshirtSize: '', merchCost: 0, attendanceCost: 0 }]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  // Fetch event and merchandise data
  useEffect(() => {
    if (!familyData || !eventId) return;
    const fetchEventData = async () => {
      try {
        const eventDocRef = doc(db, `families/${familyData.id}/events/${eventId}`);
        const eventSnap = await getDoc(eventDocRef);
        if (eventSnap.exists()) {
          const eventData = { id: eventSnap.id, ...eventSnap.data() };
          setEvent(eventData);
          const merchCollectionRef = collection(eventDocRef, 'merchandise');
          const merchSnap = await getDocs(merchCollectionRef);
          const merchList = merchSnap.docs.map(doc => ({ id: doc.id, ...doc.data() }));
          setMerchandise(merchList);
        } else { toast.error("Event not found!"); navigate('/'); }
      } catch (error) { toast.error("Failed to load event data."); console.error(error); } 
      finally { setLoading(false); }
    };
    fetchEventData();
  }, [familyData, eventId, navigate]);

  // Recalculate costs whenever attendees or their ages change
  const calculateCosts = useCallback(() => {
    if (!event || !event.feeStructure) return;

    const { type, rules, cost } = event.feeStructure;

    setAttendees(prevAttendees => {
        return prevAttendees.map(attendee => {
            let newAttendanceCost = 0;
            if (type === 'perRegistration') {
                newAttendanceCost = prevAttendees.indexOf(attendee) === 0 ? cost : 0; 
            } else if (type === 'perPerson' && attendee.age !== '') {
                const attendeeAge = parseInt(attendee.age);
                const matchingRule = rules.find(rule => attendeeAge >= rule.minAge && attendeeAge <= rule.maxAge);
                newAttendanceCost = matchingRule ? matchingRule.cost : 0;
            }
            return { ...attendee, attendanceCost: newAttendanceCost };
        });
    });
  }, [event]);
  
  // FIXED ESLINT WARNING: Extracted complex dependency
  const agesString = attendees.map(a => a.age).join();
  useEffect(() => {
    calculateCosts();
  }, [agesString, calculateCosts]);

  const totalCost = useMemo(() => {
    return attendees.reduce((total, current) => total + current.merchCost + current.attendanceCost, 0);
  }, [attendees]);

  const addAttendee = () => {
    setAttendees([...attendees, { id: uuidv4(), name: '', age: '', tshirtName: '', tshirtSize: '', merchCost: 0, attendanceCost: 0 }]);
  };

  const removeAttendee = (id) => {
    const newAttendees = attendees.filter(a => a.id !== id);
    setAttendees(newAttendees);
    if (event?.feeStructure?.type === 'perRegistration') {
        calculateCosts();
    }
  };

  const handleAttendeeChange = (id, field, value) => {
    setAttendees(attendees.map(a => a.id === id ? { ...a, [field]: value } : a));
  };

  const handleMerchChange = (id, itemName, size, upcharge) => {
    const tShirt = merchandise.find(item => item.itemName === itemName);
    const tShirtCost = tShirt ? tShirt.basePrice : 0;
    const totalItemCost = size ? tShirtCost + upcharge : 0;
    
    setAttendees(attendees.map(a => a.id === id ? { ...a, tshirtName: itemName, tshirtSize: size, merchCost: totalItemCost } : a));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    const formattedAttendees = attendees.map(({ id, attendanceCost, merchCost, ...rest }) => ({
        ...rest,
        cost: attendanceCost + merchCost,
    }));
    try {
        const registrationCollectionRef = collection(db, `families/${familyData.id}/events/${eventId}/registrations`);
        await addDoc(registrationCollectionRef, {
            registeredBy: userData.uid,
            registrationDate: serverTimestamp(),
            attendees: formattedAttendees,
            totalCost: totalCost,
            paymentStatus: "Pending",
        });
        toast.success("Registration submitted! Payment step is next.");
        navigate(`/event/${eventId}`);
    } catch (error) { toast.error("Failed to submit registration."); console.error(error); } 
    finally { setSubmitting(false); }
  };

  const isRegistrationOpen = () => {
      if (!event) return false;
      if (event.registrationStatus === 'closed') return false;
      if (event.registrationStatus === 'scheduled') {
          const today = new Date();
          today.setHours(0, 0, 0, 0);
          
          const [year, month, day] = event.registrationOpenDate.split('-');
          const openDate = new Date(year, month - 1, day);
          openDate.setHours(0,0,0,0);

          if (today < openDate) return false;
      }
      return true;
  };

  if (loading) { return <Layout><div className="flex justify-center py-20"><Spinner size="lg" /></div></Layout>; }

  return (
    <Layout>
      <div className="max-w-4xl mx-auto">
        <div className="bg-white rounded-lg shadow-md p-6 md:p-8 mb-8">
            <Link to={`/event/${eventId}`} className="text-sm text-primary hover:underline mb-4 inline-block">&larr; Back to Event Details</Link>
            <h1 className="text-3xl font-bold text-neutral-gray">Register for {event?.eventName}</h1>
        </div>

        {!isRegistrationOpen() ? (
            <div className="bg-white p-12 rounded-lg shadow-md text-center border-t-4 border-cta-orange">
                {event.registrationStatus === 'scheduled' ? (
                    <>
                        <FaCalendarDay className="mx-auto text-5xl text-cta-orange mb-4"/>
                        <h2 className="text-2xl font-bold text-gray-800 mb-2">Registration Not Open Yet</h2>
                        <p className="text-gray-600 text-lg">
                            Registration for this event will open on <br/>
                            <span className="font-bold text-primary mt-2 inline-block">
                                {format(new Date(event.registrationOpenDate.split('-')[0], event.registrationOpenDate.split('-')[1]-1, event.registrationOpenDate.split('-')[2]), 'MMMM d, yyyy')}
                            </span>.
                        </p>
                    </>
                ) : (
                    <>
                        <FaLock className="mx-auto text-5xl text-gray-400 mb-4"/>
                        <h2 className="text-2xl font-bold text-gray-800 mb-2">Registration Closed</h2>
                        <p className="text-gray-600 text-lg">Registration for this event is currently closed.</p>
                    </>
                )}
            </div>
        ) : (
            <form onSubmit={handleSubmit} className="bg-white rounded-lg shadow-md">
                <div className="p-6 md:p-8 space-y-6">
                    <h2 className="text-xl font-semibold">Attendees</h2>
                    {attendees.map((attendee) => (
                        <div key={attendee.id} className="p-4 border rounded-md relative flex flex-col md:flex-row gap-4">
                            {attendees.length > 1 && (<button type="button" onClick={() => removeAttendee(attendee.id)} className="absolute top-2 right-2 text-red-500 hover:text-red-700"><FaTrash /></button>)}
                            <div className="w-full md:w-1/3">
                                <label htmlFor={`name-${attendee.id}`} className="block text-sm font-medium text-gray-700">Full Name</label>
                                <input type="text" id={`name-${attendee.id}`} value={attendee.name} onChange={e => handleAttendeeChange(attendee.id, 'name', e.target.value)} required className="mt-1 w-full border-gray-300 rounded-md shadow-sm"/>
                            </div>
                            <div className="w-full md:w-1/4">
                                <label htmlFor={`age-${attendee.id}`} className="block text-sm font-medium text-gray-700">Age</label>
                                <input type="number" id={`age-${attendee.id}`} value={attendee.age} onChange={e => handleAttendeeChange(attendee.id, 'age', e.target.value)} required className="mt-1 w-full border-gray-300 rounded-md shadow-sm"/>
                            </div>
                            <MerchandiseSelector merchandise={merchandise} attendee={attendee} onMerchChange={(itemName, size, upcharge) => handleMerchChange(attendee.id, itemName, size, upcharge)}/>
                            <div className="w-full md:w-auto text-right md:text-left self-end md:self-center">
                                <p className="text-sm text-gray-500">Subtotal</p>
                                <p className="font-semibold text-lg">${(attendee.attendanceCost + attendee.merchCost).toFixed(2)}</p>
                            </div>
                        </div>
                    ))}
                    <button type="button" onClick={addAttendee} className="flex items-center gap-2 text-primary font-semibold hover:underline"><FaPlus size={12}/> Add Another Attendee</button>
                </div>
                
                <div className="p-6 bg-gray-50 rounded-b-lg flex flex-col md:flex-row justify-between items-center gap-4">
                    <div className="text-2xl font-bold text-neutral-gray">Total: <span className="text-success">${totalCost.toFixed(2)}</span></div>
                    <button type="submit" disabled={submitting} className="w-full md:w-auto px-8 py-3 bg-cta-orange text-white font-bold rounded-lg shadow hover:bg-orange-700 disabled:opacity-50">{submitting ? 'Submitting...' : 'Submit Registration'}</button>
                </div>
            </form>
        )}
      </div>
    </Layout>
  );
};

export default RegistrationPage;